import { useState } from 'react';

import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { Alert, AlertTitle, useTheme } from '@mui/material';
import Skeleton from '@mui/material/Skeleton';

import { Language } from '~/constants/languages';
import { useForceDatasetDownloadMutation, useGetDictionaryQuery } from '~/extension/schema';
import { trackEvent } from '~/helpers/analytics';
import { useExtension } from '~/hooks/useExtension';
import { useFirefoxExtension } from '~/hooks/useFirefoxExtension';
import { useLanguageInfo } from '~/hooks/useLanguageInfo';

import { DoctorCard } from './DoctorCard';

const { Button } = ToucanComponents;

const title = 'Dictionary Info';
enum Label {
  Health = 'Dictionary Health',
  DictionaryLanguage = 'Dictionary Language',
  Entries = 'Dictionary Entries',
}
const formatWordAmount = (wordAmount: number) => new Intl.NumberFormat('en-US').format(wordAmount);

interface DictionaryInfoProps {
  getLanguageValue: (language?: string) => string;
}

const StandardDictionaryInfo = ({ getLanguageValue }: DictionaryInfoProps) => {
  const { isInstalled, loading } = useExtension();
  const [isLoading, setLoading] = useState<boolean>(false);
  const { data, error, refetch, loading: dictionaryLoading } = useGetDictionaryQuery({
    skip: !isInstalled,
  });

  const [forceDownload, { error: mutationError }] = useForceDatasetDownloadMutation();
  const wordAmount = data?.dictionary?.size ?? 0;
  const language = getLanguageValue(data?.dictionary?.language);
  const isSameLanguage = data?.dictionary?.isSameLanguage ?? false;
  const theme = useTheme();

  if (!isInstalled) {
    return null;
  }
  return (
    <>
      {loading || dictionaryLoading ? (
        <Skeleton
          variant="rectangular"
          css={css`
            min-width: 416px;
            min-height: 246px;
            border-radius: ${theme.spacing(1)};
            &.MuiSkeleton-root {
              background-color: ${ToucanColors.gray[100]};
            }
          `}
        />
      ) : (
        <DoctorCard
          title="Dictionary Info"
          beforeContent={
            <>
              {error && (
                <Alert severity="error">
                  <AlertTitle>We ran into some issues</AlertTitle>
                  {error.message}
                </Alert>
              )}
              {mutationError && (
                <Alert severity="error">
                  <AlertTitle>We had some issues fixing the dictionary</AlertTitle>
                  {mutationError.message}
                </Alert>
              )}
            </>
          }
          asideContent={
            !isSameLanguage ||
            (wordAmount === 0 && (
              <Button
                size="small"
                onClick={async () => {
                  if (!isLoading) {
                    trackEvent({ name: 'Doctor fix dictionary', properties: { language, wordAmount } });
                    setLoading(true);
                    try {
                      await forceDownload({});
                      await refetch();
                    } catch (e) {
                      // Ignore error
                    }
                    setLoading(false);
                  }
                }}
              >
                {isLoading ? 'Fixing...' : 'Fix'}
              </Button>
            ))
          }
          rows={[
            {
              label: Label.Health,
              value: isSameLanguage,
            },
            {
              label: Label.DictionaryLanguage,
              value: language,
            },
            {
              label: Label.Entries,
              value: formatWordAmount(wordAmount),
            },
          ]}
        />
      )}
    </>
  );
};

const FirefoxFallbackDictionaryInfo = ({ getLanguageValue }: DictionaryInfoProps) => {
  const { dictionary, isInstalledInFirefox, loading } = useFirefoxExtension();

  const wordAmount = dictionary?.size ?? 0;
  const isSameLanguage = dictionary?.isSameLanguage ?? false;

  if (!isInstalledInFirefox || loading) {
    return null;
  }
  /**
   * TODO (MEOW-880):
   * - Add a "Fix" button in asideContent that allows the user to download
   * a dictionary dataset without making a Finch request from the website.
   * - Add an alert in beforeContent if the attempt to download that
   * dataset fails.
   */
  return (
    <DoctorCard
      title={title}
      rows={[
        {
          label: Label.Health,
          value: isSameLanguage,
        },
        {
          label: Label.DictionaryLanguage,
          value: getLanguageValue(dictionary?.language),
        },
        {
          label: Label.Entries,
          value: formatWordAmount(wordAmount),
        },
      ]}
    />
  );
};

export const DictionaryInfo = () => {
  const { shouldFallBack } = useExtension();
  const { getLanguageFromLanguageId } = useLanguageInfo();
  const getLanguageValue = (language?: string) =>
    language ? (getLanguageFromLanguageId(language) as Language) : 'None';
  return shouldFallBack ? (
    <FirefoxFallbackDictionaryInfo getLanguageValue={getLanguageValue} />
  ) : (
    <StandardDictionaryInfo getLanguageValue={getLanguageValue} />
  );
};
